import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import { VpnKey as KeyIcon } from '@mui/icons-material';

/**
 * GitHub PR repository settings — source of truth is /automation/v1/repo-config.
 */
export default function AutomationRepoConfigSection({
  repoConfig,
  tokenInput,
  onRepoConfigChange,
  onTokenInputChange,
  disabled = false,
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        GitHub Pull Request Repository
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This is the repository used when creating automation pull requests. Settings here
        update the same configuration as the create-pr worker.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Chip
          icon={<KeyIcon />}
          label={repoConfig.token_configured ? 'Access token configured' : 'No access token configured'}
          color={repoConfig.token_configured ? 'success' : 'default'}
          variant="outlined"
          size="small"
        />
        {repoConfig.updated_at && (
          <Typography variant="caption" color="text.secondary">
            Updated {new Date(repoConfig.updated_at).toLocaleString()}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
        <TextField
          label="GitHub Repository URL"
          value={repoConfig.repo_url || ''}
          onChange={(e) => onRepoConfigChange('repo_url', e.target.value)}
          fullWidth
          disabled={disabled}
          placeholder="https://github.com/Stratovai-Org/automation-playwright"
          helperText="HTTPS URL to the GitHub repository used for automation PRs"
        />

        <TextField
          label="Base branch"
          value={repoConfig.base_branch || ''}
          onChange={(e) => onRepoConfigChange('base_branch', e.target.value)}
          fullWidth
          disabled={disabled}
          placeholder="release/QA"
          helperText="Pull requests target this branch (case preserved)"
        />

        <TextField
          label="Automation base path"
          value={repoConfig.automation_base_path || ''}
          onChange={(e) => onRepoConfigChange('automation_base_path', e.target.value)}
          fullWidth
          disabled={disabled}
          placeholder="tests/stratovai/generated"
          helperText="Repo-relative folder for generated automation files"
        />

        <TextField
          label="Access Token"
          value={tokenInput}
          onChange={(e) => onTokenInputChange(e.target.value)}
          fullWidth
          disabled={disabled}
          type="password"
          autoComplete="new-password"
          placeholder={repoConfig.token_configured ? 'Leave blank to keep existing token' : 'ghp_xxxxxxxxxxxxxxxxxxxx'}
          helperText={
            repoConfig.token_configured
              ? 'Enter a new token only to replace the stored token'
              : 'GitHub personal access token (required when enabled)'
          }
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={Boolean(repoConfig.enabled)}
            onChange={(e) => onRepoConfigChange('enabled', e.target.checked)}
            disabled={disabled}
          />
        }
        label="Enable GitHub pull request integration for this project"
        sx={{ mt: 2, display: 'block' }}
      />
    </Box>
  );
}
